import React, {useContext, useEffect, useState} from 'react';
import {Offcanvas} from "react-bootstrap";
import {useTranslation, withTranslation} from "react-i18next";
import {ReactComponent as ArrowRightIco} from "../../../../images/icons/arrow-right.svg";
import LoginUserModal from "../../User/LoginUserModal";
import {api} from "../../../services/Api/api";
import LoadingContainer from "../../Common/LoadingContainer";
import VoucherProductComponent from "../../User/components/Voucher/VoucherProductComponent";
import UserUtils from "../../../services/User/UserUtils";
import {connect, DispatchProp} from "react-redux";
import {RootState} from "../../../../reducers";
import {getUser} from "../../../services/User/reducer";
import VoucherUtils from "../../../services/User/VoucherUtils";
import VoucherSingleComponent from "../../User/components/Voucher/VoucherSingleComponent";
import DrawerVouchersCoupon from "./DrawerVouchersCoupon";
import {getCart} from "../../../services/Cart/reducer";
import IframeWidget from "../../../utils/IframeWidget";
import {getStoreConfig, getStoreConfigLoyalty} from "../../../services/Store/reducer";
import StoreUtils from "../../../services/Store/StoreUtils";
import {VoucherApi} from "../../../services/Api/types";
import {VouchersContext} from "../../../services/Vouchers/VouchersContext";

interface OwnProps {
    show: boolean;
    onHide: () => void;
    hideCouponBox?: boolean;
    customHeader?: JSX.Element;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;

const getFilteredUserVouchers = (
    vouchers: {
        voucher: VoucherApi,
        promotion_id: any
    }[],
    configLoyalty: Record<string, any> | undefined
) => {
    const sorted = [...vouchers].sort((a, b) => a.voucher.name.localeCompare(b.voucher.name));
    const active = sorted.filter(x => (x.voucher.status === "ACTIVE" || x.promotion_id !== undefined));

    if (configLoyalty?.qrcode) {
        return active;
    }

    return active.filter(voucher => voucher.voucher.supported_online);
};

const DrawerVouchers = ({
                            show,
                            onHide,
                            user,
                            cart,
                            hideCouponBox,
                            customHeader,
                            configLoyalty,
                            config
                        }: Props) => {
    const {t} = useTranslation();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);
    const [products, setProducts] = useState<any[] | undefined>();
    const {syncVouchers, vouchers, loadingVouchers} = useContext(VouchersContext);
    const activeVouchers = VoucherUtils.getVoucherWithCarts(vouchers, cart);
    const userVouchers = getFilteredUserVouchers(activeVouchers, configLoyalty);
    const onlineVouchers = userVouchers.filter(voucher => voucher.voucher.supported_online);
    const offlineVouchers = userVouchers.filter(voucher => !voucher.voucher.supported_online);

    useEffect(() => {
        initOnShow();
    }, [show, user.id]);

    const initOnShow = async () => {
        if (show) {
            await syncVouchers(user.id, api.placeId);
            await fetchProducts();

        } else {
            setProducts(undefined)
        }
    }

    const fetchProducts = async () => {
        try {
            const res = await api.getProducts();
            setProducts(res);
        } catch (error) {
            setProducts([]);
        }
    }

    const getFilteredProducts = (free?: boolean) => {
        if (!products) return [];

        if (free) {
            return products.filter((product) => !product.cost);
        }
        return products.filter((product) => product.cost);
    }

    const handleHide = () => {
        IframeWidget.onHide(onHide);
    }

    return (
        <>
            {showLoginModal && (
                <LoginUserModal onHide={() => setShowLoginModal(false)} show/>
            )}
            {showRegistrationModal && (
                <LoginUserModal page="REGISTER" onHide={() => setShowRegistrationModal(false)} show/>
            )}
            <Offcanvas placement="end" show={show} onShow={() => IframeWidget.onShow()}
                       onHide={handleHide} className='drawer vouchers-drawer'>
                {!customHeader && (
                    <Offcanvas.Header closeButton aria-label={t("common.action.close")}>
                        <Offcanvas.Title>
                            {hideCouponBox ? t("modules.navbar.field.vouchers.title") : t("modules.navbar.field.coupon_and_vouchers.title")}
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                )}
                <Offcanvas.Body>
                    {customHeader ? customHeader : <></>}
                    {!hideCouponBox && !StoreUtils.isOrderingReadonly(config) &&
                        <DrawerVouchersCoupon onHide={handleHide}/>}
                    {
                        UserUtils.isLoyaltyEnabled(configLoyalty) && (
                            <>
                                <div
                                    className={`d-flex flex-column mb-3 ${hideCouponBox || StoreUtils.isOrderingReadonly(config) ? "mt-0" : "mt-5"}`}>
                                    <h5 className="mb-2">{t("modules.navbar.field.your_vouchers.title")}</h5>
                                    <small>{t("modules.navbar.field.your_vouchers.description")}</small>
                                </div>
                                {
                                    UserUtils.isLogged(user) ? (
                                        loadingVouchers ? <LoadingContainer/> : onlineVouchers.length > 0 && (
                                            <div className="d-flex flex-column gap-3 mt-2">
                                                {
                                                    onlineVouchers.map((voucher) => {
                                                            return (
                                                                <div className={"col d-flex"} key={voucher.voucher.id}>
                                                                    <VoucherSingleComponent voucher={voucher.voucher}
                                                                                            promotion_id={voucher.promotion_id}/>
                                                                </div>
                                                            )
                                                        }
                                                    )
                                                }
                                            </div>
                                        )
                                    ) : (
                                        <ul className="navbar-nav justify-content-end flex-grow-1 mt-3">
                                            <li className={"nav-item"}>
                                                <button className="nav-link" onClick={() => setShowLoginModal(true)}>
                                                    <span>{t("common.action.login")}</span>
                                                    <ArrowRightIco/>
                                                </button>
                                            </li>
                                            <li className={"nav-item"}>
                                                <button className="nav-link register-btn"
                                                        onClick={() => setShowRegistrationModal(true)}>
                                                    <span>{t("common.action.create_an_account")}</span>
                                                    <ArrowRightIco/>
                                                </button>
                                            </li>
                                        </ul>
                                    )
                                }
                                {!products ? <LoadingContainer/> : (
                                    <>
                                        {products.length > 0 && (
                                            <div
                                                className={`d-flex flex-column gap-5 ${UserUtils.isLogged(user) ? "mt-5" : "mt-0"}`}>
                                                {getFilteredProducts(true).length > 0 && (
                                                    <div className="d-flex flex-column gap-3">
                                                        <h5>{t("modules.navbar.field.free_vouchers.title")}</h5>
                                                        {getFilteredProducts(true).map(product => (
                                                            <VoucherProductComponent key={product.id} product={product}
                                                                                     onSuccessUserModal={onHide}/>
                                                        ))}
                                                    </div>
                                                )}
                                                {getFilteredProducts().length > 0 && (
                                                    <div className="d-flex flex-column gap-3">
                                                        <h5>{t("modules.navbar.field.vouchers_to_buy.title")}</h5>
                                                        {getFilteredProducts().map(product => (
                                                            <VoucherProductComponent key={product.id} product={product}
                                                                                     onSuccessUserModal={onHide}/>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                                {
                                    UserUtils.isLogged(user) ? (
                                        loadingVouchers ? <LoadingContainer/> : offlineVouchers.length > 0 && (
                                            <>
                                                <div
                                                    className="d-flex flex-column mb-3 mt-5">
                                                    <h5 className="mb-2">{t("modules.navbar.field.other_vouchers.title")}</h5>
                                                    <small>{t("modules.navbar.field.other_vouchers.description")}</small>
                                                </div>
                                                <div className="d-flex flex-column gap-3 mt-2">
                                                    {
                                                        offlineVouchers.map((voucher) => {
                                                                return (
                                                                    <div className={"col d-flex"} key={voucher.voucher.id}>
                                                                        <VoucherSingleComponent voucher={voucher.voucher}
                                                                                                promotion_id={voucher.promotion_id}/>
                                                                    </div>
                                                                )
                                                            }
                                                        )
                                                    }
                                                </div>
                                            </>
                                        )
                                    ) : <></>
                                }
                            </>
                        )
                    }
                </Offcanvas.Body>
            </Offcanvas>
        </>
    )
}
const mapStateToProps = (state: RootState) => ({
    user: getUser(state.user),
    cart: getCart(state.cart),
    configLoyalty: getStoreConfigLoyalty(state.config),
    config: getStoreConfig(state.config)
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(DrawerVouchers));